import React__default, { useRef, useState, useMemo, useEffect } from 'react';
import { formatMoney } from '../../utils/stringUtils.js';
import { getEntityImage } from '../../utils/assetUtils.js';
import '../../analytics/events.js';
import '../../node_modules/tslib/tslib.es6.js';
import 'uuid';
import 'lodash/includes';
import { getAppConsistentParams } from '../../utils/appUtils.js';
import '../../utils/brandclubStoreConfigUtils.js';
import 'numeral';
import '../../utils/broadcast.js';
import '../../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import '@splitsoftware/splitio-react';
import '@tanstack/react-query';
import '../../Auth/index.js';
import '../../types/Campaign.js';
import { getProductUrl } from '../../utils/productUtils.js';
import '../../api/graphQl/brand.js';
import '../../api/graphQl/product.js';
import '../../api/cache/appConfigQueryOptions.js';
import '@brandclub/types';
import 'lodash/omitBy';
import 'lodash/isNil';
import '../../utils/logger.js';

// Checkmark icon for selected state
const CheckmarkIcon = () => (React__default.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "tw:absolute tw:-top-2 tw:-right-2", "aria-hidden": "true" },
    React__default.createElement("circle", { cx: "8", cy: "8", r: "8", className: "tw:fill-primary" }),
    React__default.createElement("path", { d: "M4.5 8L7 10.5L11.5 6", stroke: "white", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })));
// Visually hidden text for screen readers
const VisuallyHidden = ({ children }) => (React__default.createElement("span", { className: "tw:sr-only" }, children));
// Card dimensions from Figma
const RECTANGLE_CARD_WIDTH = 88;
const RECTANGLE_CARD_GAP = 12;
/**
 * Get the display name for a variant from its variationNameValues
 * Prioritizes sizeName, then colorName, then first available value
 * Falls back to full title (CSS handles truncation)
 */
const getVariantDisplayName = (variant) => {
    const values = variant.variationNameValues;
    if (values && Object.keys(values).length > 0) {
        // Priority order for display name
        const priorityKeys = ['sizeName', 'colorName', 'styleName', 'patternName'];
        for (const key of priorityKeys) {
            if (values[key])
                return values[key];
        }
        // Fall back to first available value
        const firstValue = Object.values(values)[0];
        if (firstValue)
            return firstValue;
    }
    // No variation values - use full title (CSS will truncate)
    return variant.title || 'Variant';
};
/**
 * Get the first image URL for a variant
 */
const getVariantImage = (variant) => {
    if (variant.imageUrls && variant.imageUrls.length > 0) {
        return variant.imageUrls[0];
    }
    // Fall back to entity image based on SKU
    return getEntityImage(variant.stacklineSku, 'product');
};
/**
 * Sort variants alphabetically by their display name for consistent ordering
 * Uses stacklineSku as a secondary sort key to ensure stable ordering when names are equal
 */
const sortVariants = (variants) => {
    return [...variants].sort((a, b) => {
        const nameA = getVariantDisplayName(a).toLowerCase();
        const nameB = getVariantDisplayName(b).toLowerCase();
        const nameCompare = nameA.localeCompare(nameB);
        // Use stacklineSku as tiebreaker for consistent ordering
        if (nameCompare !== 0)
            return nameCompare;
        return a.stacklineSku.localeCompare(b.stacklineSku);
    });
};
/**
 * Rectangle card variant - used when all variants fit on one line
 * Shows image, price, and variant name
 */
const RectangleVariantCard = ({ variant, isSelected, onClick }) => {
    const displayName = getVariantDisplayName(variant);
    const fullTitle = variant.title || displayName;
    const imageUrl = getVariantImage(variant);
    const price = variant.retailPrice;
    const priceText = price !== undefined ? formatMoney(price) : '';
    return (React__default.createElement("button", { type: "button", onClick: onClick, title: fullTitle, "aria-pressed": isSelected, "aria-label": `${fullTitle}${priceText ? `, ${priceText}` : ''}${isSelected ? ', currently selected' : ''}`, className: `
        tw:relative tw:flex tw:flex-col tw:rounded-[8px] tw:bg-white
        tw:w-[88px] tw:shrink-0 tw:cursor-pointer tw:transition-all tw:overflow-visible
        tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:focus-visible:ring-offset-2
        ${isSelected ? 'tw:border tw:border-primary' : 'tw:border tw:border-[#DCDCDC] tw:hover:bg-[#F6F8FA]'}
      ` },
        isSelected && React__default.createElement(CheckmarkIcon, null),
        isSelected && React__default.createElement(VisuallyHidden, null, "Selected:"),
        React__default.createElement("div", { className: "tw:flex tw:items-center tw:justify-center tw:p-2 tw:pb-2" },
            React__default.createElement("div", { className: "tw:w-[72px] tw:h-[72px] tw:flex tw:items-center tw:justify-center tw:shrink-0" },
                React__default.createElement("img", { src: imageUrl, alt: "", "aria-hidden": "true", className: "tw:max-w-full tw:max-h-full tw:object-contain tw:rounded" }))),
        React__default.createElement("div", { className: `tw:w-full tw:h-px ${isSelected ? 'tw:bg-primary' : 'tw:bg-[#DCDCDC]'}` }),
        React__default.createElement("div", { className: "tw:flex tw:flex-col tw:items-start tw:px-2 tw:py-2" },
            price !== undefined && (React__default.createElement("span", { className: `tw:text-[16px] tw:text-primary tw:leading-[20px] ${isSelected ? 'tw:font-semibold' : 'tw:font-medium'}`, "aria-hidden": "true" }, priceText)),
            React__default.createElement("span", { className: `tw:text-[14px] tw:text-primary tw:leading-[20px] tw:text-left tw:w-full tw:line-clamp-2 ${isSelected ? 'tw:font-semibold' : 'tw:font-medium'}`, "aria-hidden": "true" }, displayName))));
};
/**
 * Compact pill variant - used when variants need to wrap to multiple lines
 * Shows small image and variant name only (no price)
 */
const CompactVariantPill = ({ variant, isSelected, onClick }) => {
    const displayName = getVariantDisplayName(variant);
    const fullTitle = variant.title || displayName;
    const imageUrl = getVariantImage(variant);
    return (React__default.createElement("button", { type: "button", onClick: onClick, title: fullTitle, "aria-pressed": isSelected, "aria-label": `${fullTitle}${isSelected ? ', currently selected' : ''}`, className: `
        tw:flex tw:items-center tw:gap-2 tw:rounded-[8px] tw:bg-white tw:py-1.5 tw:pl-1.5 tw:pr-3
        tw:cursor-pointer tw:transition-all tw:max-w-[200px]
        tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:focus-visible:ring-offset-2
        ${isSelected ? 'tw:border tw:border-primary' : 'tw:border tw:border-[#DCDCDC] tw:hover:bg-[#F6F8FA]'}
      ` },
        isSelected && React__default.createElement(VisuallyHidden, null, "Selected:"),
        React__default.createElement("div", { className: "tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:shrink-0" },
            React__default.createElement("img", { src: imageUrl, alt: "", "aria-hidden": "true", className: "tw:max-w-full tw:max-h-full tw:object-contain tw:rounded" })),
        React__default.createElement("span", { className: `tw:text-sm tw:text-primary tw:truncate ${isSelected ? 'tw:font-semibold' : 'tw:font-medium'}`, "aria-hidden": "true" }, displayName)));
};
/**
 * ProductVariantPicker - Adaptive component that switches between rectangle cards
 * and compact pills based on available container width
 */
const ProductVariantPicker = ({ currentStacklineSku, variants, onVariantSelect, navigate, }) => {
    const containerRef = useRef(null);
    const [useCompactLayout, setUseCompactLayout] = useState(false);
    // Sort variants for consistent ordering across pages
    const sortedVariants = useMemo(() => sortVariants(variants), [variants]);
    // Calculate if we need compact layout based on container width
    useEffect(() => {
        if (!containerRef.current)
            return;
        const checkLayout = () => {
            var _a;
            const containerWidth = ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
            const totalCardsWidth = sortedVariants.length * RECTANGLE_CARD_WIDTH + (sortedVariants.length - 1) * RECTANGLE_CARD_GAP;
            // Use compact layout if cards don't fit in one line
            setUseCompactLayout(totalCardsWidth > containerWidth);
        };
        // Initial check
        checkLayout();
        // Set up ResizeObserver for responsive behavior
        const resizeObserver = new ResizeObserver(checkLayout);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [sortedVariants.length]);
    // Don't render if no variants or only one variant (nothing to switch to)
    if (!variants || variants.length <= 1) {
        return null;
    }
    const handleVariantClick = (variant) => {
        const { stacklineSku } = variant;
        // Don't navigate if clicking the already selected variant
        if (stacklineSku === currentStacklineSku)
            return;
        // Use callback if provided (Concierge widget pattern)
        if (onVariantSelect) {
            onVariantSelect(stacklineSku);
            return;
        }
        // Use direct navigation if navigate is provided (PDP pattern)
        if (navigate) {
            const urlSearchParams = getAppConsistentParams(window.location.search);
            const targetUrl = getProductUrl({
                title: variant.title,
                stacklineSku: variant.stacklineSku,
                urlSearchParams,
            });
            navigate({
                pathname: targetUrl.split('?')[0],
                search: targetUrl.split('?')[1],
            });
        }
    };
    return (React__default.createElement("div", { ref: containerRef, className: "tw:w-full tw:mb-5", id: "product-variant-picker" },
        React__default.createElement("div", { role: "group", "aria-label": `Product variants, ${sortedVariants.length} options available`, className: `
          tw:flex tw:flex-wrap tw:items-stretch tw:pr-2
          ${useCompactLayout ? 'tw:gap-2' : 'tw:gap-3'}
        ` }, sortedVariants.map((variant) => {
            const isSelected = variant.stacklineSku === currentStacklineSku;
            if (useCompactLayout) {
                return (React__default.createElement(CompactVariantPill, { key: variant.stacklineSku, variant: variant, isSelected: isSelected, onClick: () => handleVariantClick(variant) }));
            }
            return (React__default.createElement(RectangleVariantCard, { key: variant.stacklineSku, variant: variant, isSelected: isSelected, onClick: () => handleVariantClick(variant) }));
        }))));
};

export { ProductVariantPicker };
//# sourceMappingURL=ProductVariantPicker.js.map
